import { useContext, useState } from 'react';
import { useLocation } from 'react-router';
import { REDIRECT_PATH_KEY } from '../../common/constants/commonConstants.js';
import { checkUserExists } from '../../common/fetch';
import { useDebounce } from '../../common/hooks/useDebounce';
import { useAuth } from '../../public/site/AuthProvider.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { RegistrationScene } from './RegistrationScene.js';
import { useRegistration } from './useRegistration.js';

export const userDetailsKey = 'registration-user-details';
export const userCredentialsKey = 'registration-user-credentials';

export const Registration = () => {
  const { siteLanguage } = useContext(SiteContext);
  const { externalAuthErrors, setExternalAuthErrors } = useAuth();
  const {
    errors: registerErrors,
    registrationInProgress,
    registrationCompleted,
    isInExternalSystem,
    registerUser,
  } = useRegistration();
  const location = useLocation();

  const [validationErrors, setValidationErrors] = useState<{ email?: boolean }>({ email: undefined });

  const debouncedCheckUserExists = useDebounce(
    async (identityCertificate: string, businessId: string, email: string) => {
      const resp = await checkUserExists(identityCertificate, businessId, email);
      setValidationErrors({ email: resp.exists });
    },
    400
  );

  const persistedCompany =
    sessionStorage.getItem('registration-company') && JSON.parse(sessionStorage.getItem('registration-company')!);
  const persistedUserDetails =
    sessionStorage.getItem(userDetailsKey) && JSON.parse(sessionStorage.getItem(userDetailsKey)!);
  const persistedUserCredentials =
    sessionStorage.getItem(userCredentialsKey) && JSON.parse(sessionStorage.getItem(userCredentialsKey)!);

  return (
    <RegistrationScene
      errors={registerErrors.length ? registerErrors : externalAuthErrors}
      step1Props={{ persistedCompany }}
      step3Props={{ validationErrors, registrationInProgress }}
      registrationCompleted={persistedUserCredentials ? true : registrationCompleted}
      persistedUserCredentials={persistedUserCredentials}
      persistedUserDetails={persistedUserDetails}
      redirectPath={new URLSearchParams(location?.search).get(REDIRECT_PATH_KEY) || undefined}
      storeDetails={(key, details) => {
        if (typeof details === 'object') {
          sessionStorage.setItem(key, JSON.stringify(details));
        } else {
          sessionStorage.setItem(key, details);
        }
      }}
      invalidateAuthSession={() => {
        sessionStorage.removeItem(userDetailsKey);
        setExternalAuthErrors([]);
      }}
      registerUser={registerUser}
      userExists={debouncedCheckUserExists}
      navigate={url => (window.location.href = url)}
      locale={siteLanguage}
      isInExternalSystem={isInExternalSystem}
    />
  );
};
